import React, { useState, useEffect } from "react";
import { Form, Row, Col, Spinner } from "react-bootstrap";
import { PageHeader, AppButton, AppBadge, AppCard, AppCardContent, FormActionBar, ResetButton } from "../ui";
import { Code2 } from "lucide-react";
import TestCaseList from "./TestCaseList";
import TestCaseForm from "./TestCaseForm";
import TagSelector from "./TagSelector";
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import BulletList from '@tiptap/extension-bullet-list';
import OrderedList from '@tiptap/extension-ordered-list';
import ListItem from '@tiptap/extension-list-item';
import Bold from '@tiptap/extension-bold';
import Italic from '@tiptap/extension-italic';
import { fetchJudgeLanguages, toCategoryOptions } from '../../utils/judgeLanguages';

const DIFFICULTIES = ["Easy", "Medium", "Hard"];

const emptyQuestion = {
  title: "",
  description: "",
  categories: [],
  labels: [],
  difficulty: "",
  sampleCode: {},
  solution: {},
  testCases: [],
  isPublic: false,
};

const QuestionForm = ({ question, onSave, onCancel, mode = "institute" }) => {
  const [form, setForm] = useState(emptyQuestion);
  const [labelsInput, setLabelsInput] = useState("");
  const [showTestCaseModal, setShowTestCaseModal] = useState(false);
  const [editingTestCase, setEditingTestCase] = useState(null);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Bold,
      Italic,
      BulletList,
      OrderedList,
      ListItem,
    ],
    content: form.description,
    onUpdate({ editor }) {
      setForm(prev => ({ ...prev, description: editor.getHTML() }));
    },
  });

  useEffect(() => {
    fetchJudgeLanguages()
      .then(raw => setCategoryOptions(toCategoryOptions(raw)))
      .finally(() => setLoadingCategories(false));
  }, []);

  useEffect(() => {
    if (question) {
      setForm({
        ...emptyQuestion,
        ...question,
        sampleCode: question.sampleCode || {},
        solution: question.solution || {},
        testCases: question.testCases || [],
        categories: question.categories || [],
        labels: question.labels || [],
      });
      setLabelsInput(question.labels ? question.labels.join(", ") : "");
    } else {
      setForm(emptyQuestion);
      setLabelsInput("");
    }
  }, [question]);

  useEffect(() => {
  if (editor && form.description !== editor.getHTML()) {
    editor.commands.setContent(form.description || "");
  }
  // eslint-disable-next-line
}, [form.description, editor]);

  const handleLabelsChange = (e) => {
    const input = e.target.value;
    setLabelsInput(input);
    setForm((prev) => ({
      ...prev,
      labels: input
        .split(",")
        .map((l) => l.trim())
        .filter(Boolean),
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleCategoriesChange = (selected) => {
    setForm((prev) => {
      const newSampleCode = {};
      const newSolution = {};
      const categoriesWithIds = selected.map(name => {
        // Check if already an object (from existing form data)
        if (typeof name === 'object' && name.name) return name;
        const opt = categoryOptions.find(o => (typeof o === 'string' ? o : o.value) === name);
        return { id: opt?.id, name };
      });
      selected.forEach((lang) => {
        const langName = typeof lang === 'object' ? lang.name : lang;
        newSampleCode[langName] = prev.sampleCode[langName] || "";
        newSolution[langName] = prev.solution[langName] || "";
      });
      return {
        ...prev,
        categories: categoriesWithIds,
        sampleCode: newSampleCode,
        solution: newSolution,
      };
    });
  };

  const handleCodeChange = (lang, type, value) => {
    setForm((prev) => ({
      ...prev,
      [type]: { ...prev[type], [lang]: value },
    }));
  };

  // Handlers for test case modal
  const handleAddTestCase = () => {
    setEditingTestCase(null);
    setShowTestCaseModal(true);
  };
  const handleEditTestCase = (tc) => {
    setEditingTestCase(tc);
    setShowTestCaseModal(true);
  };
  const handleDeleteTestCase = (tcId) => {
    setForm((prev) => ({
      ...prev,
      testCases: prev.testCases.filter((t) => t._id !== tcId),
    }));
  };
  const handleSaveTestCase = (tc) => {
    const isEditing = !!editingTestCase;
    setForm((prev) => {
      const prevCases = prev.testCases || [];
      let newList;
      if (isEditing) {
        newList = prevCases.map((t) =>
          String(t._id) === String(tc._id) ? tc : t
        );
      } else {
        newList = [...prevCases, { ...tc, _id: Date.now().toString() }];
      }
      return { ...prev, testCases: newList };
    });
    setShowTestCaseModal(false);
    setEditingTestCase(null);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const labels = labelsInput
      .split(",")
      .map((l) => l.trim())
      .filter(Boolean);

    // Remove _id from testCases before sending to backend
    const cleanedTestCases = (form.testCases || []).map(({ _id, ...rest }) => rest);

    const payload = {
      ...form,
      labels,
      testCases: cleanedTestCases,
    };
    // Only include isPublic when this form is being used in the platform
    // library context — institute users must never set this flag.
    if (mode !== "library") delete payload.isPublic;

    onSave(payload);
  };

  // Restore the form to its pristine state (the question being edited, or a
  // blank question when adding). Mirrors the initialisation effect above.
  const handleReset = () => {
    if (question) {
      setForm({
        ...emptyQuestion,
        ...question,
        sampleCode: question.sampleCode || {},
        solution: question.solution || {},
        testCases: question.testCases || [],
        categories: question.categories || [],
        labels: question.labels || [],
      });
      setLabelsInput(question.labels ? question.labels.join(", ") : "");
    } else {
      setForm(emptyQuestion);
      setLabelsInput("");
    }
  };


  return (
    <div className="tw-flex tw-flex-col tw-gap-5 has-form-actionbar">
      <PageHeader
        icon={Code2}
        title={question && question._id ? "Edit Coding Question" : "Add Coding Question"}
        description={question && question._id ? "Update the question details below" : "Fill in the details to add a new coding question"}
      />
      {/* `question-form` is load-bearing, not decorative: styles/base/_elements.scss
          keys the ProseMirror caret rules on it, and the caret cannot be re-enabled
          from here because `body *` blanks it with !important.

          The four reach-ins style react-bootstrap's own classes, and they are all
          that survived of this form's old block: background, ink, border, placeholder
          and the focus ring are set by _bootstrap-skin.scss with !important, so the
          hardcoded #3b82f6 focus this file used to declare never rendered. */}
      <Form
        onSubmit={handleSubmit}
        className={
          "question-form tw-flex tw-flex-col tw-gap-6 " +
          "[&_.form-label]:tw-font-medium [&_.form-label]:tw-text-content " +
          "[&_.form-control]:tw-rounded-sm [&_.form-select]:tw-rounded-sm " +
          "[&_.form-control]:tw-text-[15px] [&_.form-select]:tw-text-[15px]"
        }
      >
        {/* Basic Information */}
        <AppCard>
          <AppCardContent>
            <h6 className="tw-mb-4 tw-text-[0.8125rem] tw-font-semibold tw-text-content-muted tw-uppercase tw-tracking-[0.05em]">
              Basic Information
            </h6>
            <Row className="gy-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Title</Form.Label>
                  <Form.Control
                    name="title"
                    value={form.title}
                    onChange={handleChange}
                    required
                    autoFocus
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Difficulty</Form.Label>
                  <Form.Select
                    name="difficulty"
                    value={form.difficulty}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select</option>
                    {DIFFICULTIES.map((d) => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  {loadingCategories ? (
                    <div className="tw-flex tw-items-center tw-gap-2">
                      <Spinner size="sm" animation="border" />
                      <span className="tw-text-content-muted tw-text-sm">Loading languages…</span>
                    </div>
                  ) : (
                    <TagSelector
                      options={categoryOptions}
                      value={(form.categories || []).map(c => typeof c === 'object' ? c.name : c)}
                      onChange={handleCategoriesChange}
                      label="Categories"
                      placeholder="Select tech stacks"
                      multiple
                      className="coding-question-category-selector"
                    />
                  )}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Labels (comma-separated)</Form.Label>
                  <Form.Control
                    type="text"
                    value={labelsInput}
                    onChange={handleLabelsChange}
                    placeholder="e.g., Sliding Window, DP, Two Pointers"
                  />
                  <div className="tw-mt-2 tw-flex tw-flex-wrap tw-gap-1">
                    {form.labels.map((l) => (
                      <AppBadge color="blue" key={l}>{l}</AppBadge>
                    ))}
                  </div>
                </Form.Group>
              </Col>
              {mode === "library" && (
                <Col md={12}>
                  <Form.Group>
                    <Form.Check
                      type="switch"
                      id="cqb-is-public"
                      label="Make this question public — visible to every institute"
                      checked={!!form.isPublic}
                      onChange={(e) => setForm((f) => ({ ...f, isPublic: e.target.checked }))}
                    />
                  </Form.Group>
                </Col>
              )}
            </Row>
          </AppCardContent>
        </AppCard>

        {/* Description */}
        <AppCard>
          <AppCardContent>
            <h6 className="tw-mb-4 tw-text-[0.8125rem] tw-font-semibold tw-text-content-muted tw-uppercase tw-tracking-[0.05em]">
              Description
            </h6>
            <Form.Group>
              {/* overflow-visible so a toolbar button's focus outline is not clipped. */}
              <div className="tw-border tw-border-line tw-rounded-sm tw-overflow-visible tw-bg-input focus-within:tw-border-primary focus-within:tw-shadow-focus">
                <div className="tw-flex tw-gap-2 tw-px-3 tw-py-2 tw-bg-surface-alt tw-border-b tw-border-line tw-rounded-t-sm">
                  <AppButton
                    size="sm"
                    variant={editor?.isActive('bold') ? 'default' : 'secondary'}
                    onMouseDown={e => { e.preventDefault(); editor?.chain().focus().toggleBold().run(); }}
                  >
                    <b>B</b>
                  </AppButton>
                  <AppButton
                    size="sm"
                    variant={editor?.isActive('italic') ? 'default' : 'secondary'}
                    onMouseDown={e => { e.preventDefault(); editor?.chain().focus().toggleItalic().run(); }}
                  >
                    <i>I</i>
                  </AppButton>
                  <AppButton
                    size="sm"
                    variant={editor?.isActive('bulletList') ? 'default' : 'secondary'}
                    onMouseDown={e => { e.preventDefault(); editor?.chain().focus().toggleBulletList().run(); }}
                  >
                    • List
                  </AppButton>
                  <AppButton
                    size="sm"
                    variant={editor?.isActive('orderedList') ? 'default' : 'secondary'}
                    onMouseDown={e => { e.preventDefault(); editor?.chain().focus().toggleOrderedList().run(); }}
                  >
                    1. List
                  </AppButton>
                </div>
                {/* Reach-ins for TipTap's own markup, which this file does not own.
                    The focused border names BOTH classes so it out-specifies the
                    `border-0` beside it — at equal specificity the winner would be
                    Tailwind's emission order, where `border-0` comes after `border`. */}
                <EditorContent
                  editor={editor}
                  value={form.description}
                  className={
                    "tw-p-3 tw-min-h-40 tw-max-h-[600px] tw-resize-y tw-overflow-auto tw-text-content tw-rounded-b-sm " +
                    "[&_.ProseMirror]:tw-outline-none [&_.ProseMirror]:tw-min-h-full " +
                    "[&_.ProseMirror]:tw-px-2 [&_.ProseMirror]:tw-py-1 [&_.ProseMirror]:tw-border-0 " +
                    "[&_.ProseMirror.ProseMirror-focused]:[border:1px_solid_var(--border-primary)] " +
                    "[&_.ProseMirror_p]:tw-mt-0 [&_.ProseMirror_p]:tw-mx-0 [&_.ProseMirror_p]:tw-mb-2 " +
                    "[&_.ProseMirror_ul]:tw-pl-6 [&_.ProseMirror_ol]:tw-pl-6 " +
                    "[&_.ProseMirror_ul]:tw-mb-2 [&_.ProseMirror_ol]:tw-mb-2"
                  }
                />
              </div>
            </Form.Group>
          </AppCardContent>
        </AppCard>

        {/* Code Templates */}
        {(form.categories || []).length > 0 && (
          <AppCard>
            <AppCardContent>
              <h6 className="tw-mb-4 tw-text-[0.8125rem] tw-font-semibold tw-text-content-muted tw-uppercase tw-tracking-[0.05em]">
                Code Templates
              </h6>
              <Row className="gy-3">
                {(form.categories || []).map((cat) => {
                  const lang = typeof cat === 'object' ? cat.name : cat;
                  return (
                  <React.Fragment key={lang}>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Sample Code ({lang})</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          value={(form.sampleCode || {})[lang] || ""}
                          onChange={(e) => handleCodeChange(lang, "sampleCode", e.target.value)}
                          placeholder={`Enter sample code for ${lang}`}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Solution ({lang}) - optional</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          value={(form.solution || {})[lang] || ""}
                          onChange={(e) => handleCodeChange(lang, "solution", e.target.value)}
                          placeholder={`Enter solution code for ${lang}`}
                        />
                      </Form.Group>
                    </Col>
                  </React.Fragment>
                  );
                })}
              </Row>
            </AppCardContent>
          </AppCard>
        )}

        {/* Test Cases */}
        <AppCard>
          <AppCardContent>
            <TestCaseList
              testCases={form.testCases || []}
              onAdd={handleAddTestCase}
              onEdit={handleEditTestCase}
              onDelete={handleDeleteTestCase}
            />
          </AppCardContent>
        </AppCard>

        {/* Footer Actions */}
        <FormActionBar>
          <AppButton type="button" variant="outline" onClick={onCancel}>
            Cancel
          </AppButton>
          <ResetButton onReset={handleReset} />
          <AppButton type="submit">
            {question ? "Save Changes" : "Add Question"}
          </AppButton>
        </FormActionBar>
      </Form>
      <TestCaseForm
        show={showTestCaseModal}
        onHide={() => { setShowTestCaseModal(false); setEditingTestCase(null); }}
        onSave={handleSaveTestCase}
        testCase={editingTestCase}
      />
    </div>
  );
};

export default QuestionForm;
